import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiLock, FiTruck, FiShield, FiFileText, FiBriefcase, FiUser, FiMapPin } from 'react-icons/fi';
import toast from 'react-hot-toast';
import useCartStore from '../store/useCartStore';
import useAuthStore from '../store/useAuthStore';
import axios from '../utils/axios';
import { ENDPOINTS } from '../config/api';
import PlaceholderImage from '../components/PlaceholderImage';
import { getProductImage } from '../utils/imageHelpers';
import { formatPrice } from '../utils/formatPrice';

const Checkout = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuthStore();
  const { cart, fetchCart, clearCart, getTotalPrice } = useCartStore();
  const [loading, setLoading] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState(null);
  const [userAddresses, setUserAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState('new');
  const [formData, setFormData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    phone: '',
    city: '',
    district: '',
    addressLine: '',
    postalCode: '',
    invoiceType: 'individual', // 'individual' | 'corporate'
    tcNo: '',
    companyName: '',
    taxOffice: '',
    taxNumber: '',
    isSeparateBillingAddress: false,
    billingFirstName: '',
    billingLastName: '',
    billingPhone: '',
    billingCity: '',
    billingDistrict: '',
    billingAddressLine: '',
    billingPostalCode: '',
    paymentMethod: 'credit-card',
    customerNote: '',
    cardholdername: '',
    cardnumber: '',
    cardexpiredatemonth: '',
    cardexpiredateyear: '',
    cardcvv2: '',
    installment: ''
  });

  useEffect(() => {
    if (!isAuthenticated) {
      toast.error(t('checkout.loginRequired', 'Satın alma işlemi için giriş yapmanız veya kayıt olmanız gerekmektedir.'));
      navigate('/login?redirect=/checkout');
      return;
    }
    fetchCart();
    fetchPaymentMethods();
    if (user) {
      fetchUserAddresses();
    }
  }, [isAuthenticated, user]);

  const fetchUserAddresses = async () => {
    try {
      const response = await axios.get(ENDPOINTS.USER.ADDRESSES);
      const addrList = response.data?.data || response.data || [];
      setUserAddresses(addrList);
      
      const defaultAddr = addrList.find(a => a.isDefault) || addrList[0];
      if (defaultAddr) {
        setSelectedAddressId(defaultAddr._id);
        applyAddressToForm(defaultAddr);
      }
    } catch (error) {
      console.error('Adresler yüklenemedi:', error);
    }
  };

  const applyAddressToForm = (addr) => {
    setFormData(prev => ({
      ...prev,
      firstName: addr.firstName || prev.firstName,
      lastName: addr.lastName || prev.lastName,
      phone: addr.phone || prev.phone,
      city: addr.city || prev.city,
      district: addr.district || prev.district,
      addressLine: addr.addressLine || prev.addressLine,
      postalCode: addr.postalCode || prev.postalCode
    }));
  };

  const handleSelectAddress = (addrId) => {
    setSelectedAddressId(addrId);
    if (addrId === 'new') {
      setFormData(prev => ({
        ...prev,
        phone: '',
        city: '',
        district: '',
        addressLine: '',
        postalCode: ''
      }));
    } else {
      const addr = userAddresses.find(a => a._id === addrId);
      if (addr) {
        applyAddressToForm(addr);
      }
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const response = await axios.get('/settings');
      const data = response.data?.data || response.data;
      setPaymentMethods(data.paymentMethods);

      if (data.paymentMethods) {
        if (data.paymentMethods.creditCard?.enabled) {
          setFormData(prev => ({ ...prev, paymentMethod: 'credit-card' }));
        } else if (data.paymentMethods.bankTransfer?.enabled) {
          setFormData(prev => ({ ...prev, paymentMethod: 'bank-transfer' }));
        } else if (data.paymentMethods.cashOnDelivery?.enabled) {
          setFormData(prev => ({ ...prev, paymentMethod: 'cash-on-delivery' }));
        }
      }
    } catch (error) {
      console.error('Ödeme yöntemleri yüklenemedi:', error);
    }
  };

  const items = cart?.items || [];
  const subtotal = getTotalPrice();
  const shippingCost = subtotal >= 500 ? 0 : 29.90;
  const total = subtotal + shippingCost;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.firstName || !formData.lastName || !formData.phone || !formData.city || !formData.district || !formData.addressLine) {
      toast.error('Lütfen teslimat adresi için Ad, Soyad, Telefon, Şehir, İlçe ve Adres alanlarını tam doldurun.');
      return;
    }

    if (formData.invoiceType === 'corporate') {
      if (!formData.companyName || !formData.taxOffice || !formData.taxNumber) {
        toast.error('Kurumsal fatura için Şirket Ünvanı, Vergi Dairesi ve Vergi Numarası alanlarını eksiksiz girin.');
        return;
      }
    }

    if (formData.isSeparateBillingAddress) {
      if (!formData.billingFirstName || !formData.billingLastName || !formData.billingPhone || !formData.billingCity || !formData.billingDistrict || !formData.billingAddressLine) {
        toast.error('Lütfen fatura adresi için Ad, Soyad, Telefon, Şehir, İlçe ve Adres alanlarını tam doldurun.');
        return;
      }
    }

    if (formData.paymentMethod === 'credit-card') {
      if (!formData.cardnumber || !formData.cardexpiredatemonth || !formData.cardexpiredateyear || !formData.cardcvv2) {
        toast.error('Lütfen kredi kartı bilgilerinizi (Kart No, Son Kullanma Ay/Yıl, CVC) eksiksiz girin.');
        return;
      }
    }

    setLoading(true);

    try {
      const orderData = {
        shippingAddress: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          city: formData.city,
          district: formData.district,
          addressLine: formData.addressLine,
          postalCode: formData.postalCode,
        },
        billingDetails: {
          invoiceType: formData.invoiceType,
          tcNo: formData.tcNo,
          companyName: formData.companyName,
          taxOffice: formData.taxOffice,
          taxNumber: formData.taxNumber,
          isSeparateBillingAddress: formData.isSeparateBillingAddress
        },
        billingAddress: formData.isSeparateBillingAddress ? {
          firstName: formData.billingFirstName,
          lastName: formData.billingLastName,
          email: formData.email,
          phone: formData.billingPhone,
          city: formData.billingCity,
          district: formData.billingDistrict,
          addressLine: formData.billingAddressLine,
          postalCode: formData.billingPostalCode,
        } : {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          city: formData.city,
          district: formData.district,
          addressLine: formData.addressLine,
          postalCode: formData.postalCode,
        },
        paymentMethod: formData.paymentMethod,
        customerNote: formData.customerNote,
        items: items.map(item => ({
          product: item.product?._id || item.product,
          quantity: item.quantity,
          variant: item.variant
        }))
      };

      const response = await axios.post(ENDPOINTS.ORDERS.BASE, orderData);
      const createdOrder = response.data.data;

      // Garanti BBVA Kredi Kartı / 3D POS Yönlendirmesi
      if (formData.paymentMethod === 'credit-card') {
        const garantiRes = await axios.post('/payment/garanti-init', {
          orderId: createdOrder._id,
          cardholdername: formData.cardholdername || `${formData.firstName} ${formData.lastName}`,
          cardnumber: formData.cardnumber,
          cardexpiredatemonth: formData.cardexpiredatemonth,
          cardexpiredateyear: formData.cardexpiredateyear,
          cardcvv2: formData.cardcvv2,
          installment: formData.installment
        });

        if (garantiRes.data.success && garantiRes.data.data) {
          const { gatewayUrl, inputs } = garantiRes.data.data;
          
          // Dynamically construct hidden form and auto-submit to Garanti 3D engine
          const form = document.createElement('form');
          form.method = 'POST';
          form.action = gatewayUrl;

          Object.keys(inputs).forEach(key => {
            const hiddenField = document.createElement('input');
            hiddenField.type = 'hidden';
            hiddenField.name = key;
            hiddenField.value = inputs[key];
            form.appendChild(hiddenField);
          });

          document.body.appendChild(form);
          form.submit();
          return;
        }
      }

      toast.success(t('checkout.messages.success'));

      if (user) {
        await clearCart();
        navigate(`/account/orders/${createdOrder._id}`);
      } else {
        localStorage.removeItem('guestCart');
        navigate('/order-success', {
          state: {
            orderNumber: createdOrder.orderNumber,
            email: formData.email
          }
        });
      }
    } catch (error) {
      console.error('Checkout error detail:', error.response?.data);
      const serverMsg = error.response?.data?.message || error.message || t('checkout.messages.error');
      toast.error(serverMsg, { duration: 6000 });
    } finally {
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6">
        <div className="text-center">
          <h2 className="font-serif text-3xl font-light tracking-wide text-gray-900 mb-4">
            {t('checkout.empty.title')}
          </h2>
          <p className="text-gray-600 font-light tracking-wide mb-8">
            {t('checkout.empty.description')}
          </p>
          <button onClick={() => navigate('/products')} className="btn-primary">
            {t('checkout.empty.button')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="border-b border-gray-100 py-12 px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <h1 className="font-serif text-4xl md:text-5xl font-light tracking-wide text-center">
            {t('checkout.title')}
          </h1>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Form Section */}
            <div className="lg:col-span-2 space-y-8">
              {/* Üyelik Teşvik Banner - Sadece misafir kullanıcılar için */}
              {!user && (
                <div className="bg-gradient-to-r from-primary-50 to-primary-100 border border-primary-200 rounded-lg p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <FiShield className="w-6 h-6 text-primary-600" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 mb-2">
                        {t('checkout.guestPromo.title')}
                      </h3>
                      <ul className="text-sm text-gray-700 space-y-1 mb-4">
                        <li>✓ {t('checkout.guestPromo.benefits.track')}</li>
                        <li>✓ {t('checkout.guestPromo.benefits.address')}</li>
                        <li>✓ {t('checkout.guestPromo.benefits.wishlist')}</li>
                        <li>✓ {t('checkout.guestPromo.benefits.campaigns')}</li>
                      </ul>
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={() => navigate('/register')}
                          className="btn-primary text-sm py-2 px-4"
                        >
                          {t('checkout.guestPromo.register')}
                        </button>
                        <button
                          type="button"
                          onClick={() => navigate('/login')}
                          className="btn-secondary text-sm py-2 px-4"
                        >
                          {t('checkout.guestPromo.login')}
                        </button>
                      </div>
                      <p className="text-xs text-gray-600 mt-3">
                        {t('checkout.guestPromo.guestContinue')}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Teslimat Bilgileri */}
              <div className="bg-gray-50 p-8">
                <h2 className="font-serif text-2xl font-light tracking-wide mb-6 flex items-center gap-3">
                  <FiTruck className="text-primary" />
                  {t('checkout.shipping.title')}
                </h2>

                {/* Kayıtlı Adres Seçimi */}
                {user && userAddresses.length > 0 && (
                  <div className="mb-8">
                    <label className="block text-xs font-semibold text-gray-700 tracking-wider uppercase mb-3">
                      Kayıtlı Adresleriniz
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {userAddresses.map((addr) => {
                        const isSelected = selectedAddressId === addr._id;
                        return (
                          <div
                            key={addr._id}
                            onClick={() => handleSelectAddress(addr._id)}
                            className={`p-4 border rounded-xl cursor-pointer transition-all ${
                              isSelected
                                ? 'border-gray-900 bg-white shadow-sm ring-1 ring-gray-900'
                                : 'border-gray-200 bg-white/60 hover:border-gray-400'
                            }`}
                          >
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-bold text-xs text-gray-900 uppercase tracking-wider">
                                {addr.title || 'Adres'}
                              </span>
                              {addr.isDefault && (
                                <span className="text-[10px] bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded font-medium">
                                  Varsayılan
                                </span>
                              )}
                            </div>
                            <p className="text-xs font-medium text-gray-800">
                              {addr.firstName} {addr.lastName} ({addr.phone})
                            </p>
                            <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                              {addr.addressLine}, {addr.district}/{addr.city}
                            </p>
                          </div>
                        );
                      })}

                      <div
                        onClick={() => handleSelectAddress('new')}
                        className={`p-4 border rounded-xl cursor-pointer transition-all flex items-center justify-center gap-2 border-dashed ${
                          selectedAddressId === 'new'
                            ? 'border-gray-900 bg-white shadow-sm ring-1 ring-gray-900'
                            : 'border-gray-300 bg-white/40 hover:border-gray-500'
                        }`}
                      >
                        <span className="text-xs font-semibold text-gray-700">
                          + Yeni Adres Gir / Elle Düzenle
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    placeholder={`${t('checkout.shipping.firstName')} *`}
                    required
                    className="input-field"
                  />
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    placeholder={`${t('checkout.shipping.lastName')} *`}
                    required
                    className="input-field"
                  />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder={`${t('checkout.shipping.email')} *`}
                    required
                    className="input-field"
                  />
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder={`${t('checkout.shipping.phone')} *`}
                    required
                    className="input-field"
                  />
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    placeholder={`${t('checkout.shipping.city')} *`}
                    required
                    className="input-field"
                  />
                  <input
                    type="text"
                    name="district"
                    value={formData.district}
                    onChange={handleChange}
                    placeholder={`${t('checkout.shipping.district')} *`}
                    required
                    className="input-field"
                  />
                  <input
                    type="text"
                    name="postalCode"
                    value={formData.postalCode}
                    onChange={handleChange}
                    placeholder={t('checkout.shipping.postalCode')}
                    className="input-field"
                  />
                  <textarea
                    name="addressLine"
                    value={formData.addressLine}
                    onChange={handleChange}
                    placeholder={`${t('checkout.shipping.address')} *`}
                    required
                    rows="3"
                    className="input-field md:col-span-2"
                  ></textarea>
                </div>
              </div>

              {/* Fatura Bilgileri */}
              <div className="bg-gray-50 p-8">
                <h2 className="font-serif text-2xl font-light tracking-wide mb-6 flex items-center gap-3">
                  <FiFileText className="text-primary" />
                  <span>Fatura Bilgileri</span>
                </h2>

                {/* Fatura Tipi Seçimi (Bireysel / Kurumsal) */}
                <div className="grid grid-cols-2 gap-3 mb-6">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, invoiceType: 'individual' })}
                    className={`py-3 px-4 rounded-xl text-xs font-bold uppercase tracking-wider flex items-center justify-center gap-2 border transition-all ${
                      formData.invoiceType === 'individual'
                        ? 'border-gray-900 bg-white text-gray-900 shadow-sm ring-1 ring-gray-900'
                        : 'border-gray-200 bg-white/60 text-gray-500 hover:border-gray-400'
                    }`}
                  >
                    <FiUser className="text-base" />
                    <span>Bireysel Fatura</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, invoiceType: 'corporate' })}
                    className={`py-3 px-4 rounded-xl text-xs font-bold uppercase tracking-wider flex items-center justify-center gap-2 border transition-all ${
                      formData.invoiceType === 'corporate'
                        ? 'border-gray-900 bg-white text-gray-900 shadow-sm ring-1 ring-gray-900'
                        : 'border-gray-200 bg-white/60 text-gray-500 hover:border-gray-400'
                    }`}
                  >
                    <FiBriefcase className="text-base" />
                    <span>Kurumsal Fatura</span>
                  </button>
                </div>

                {/* Bireysel Özel Alanlar */}
                {formData.invoiceType === 'individual' && (
                  <div className="mb-6">
                    <input
                      type="text"
                      name="tcNo"
                      value={formData.tcNo}
                      onChange={handleChange}
                      maxLength="11"
                      placeholder="T.C. Kimlik Numarası (Opsiyonel / e-Fatura için)"
                      className="input-field"
                    />
                    <p className="text-[11px] text-gray-500 mt-1.5 pl-1 font-light">
                      e-Fatura ve e-Arşiv faturanızın adınıza düzenlenmesi için TC kimlik numaranızı girebilirsiniz.
                    </p>
                  </div>
                )}

                {/* Kurumsal Özel Alanlar */}
                {formData.invoiceType === 'corporate' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 animate-fadeIn">
                    <input
                      type="text"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleChange}
                      placeholder="Şirket Tam Ünvanı *"
                      required={formData.invoiceType === 'corporate'}
                      className="input-field md:col-span-2"
                    />
                    <input
                      type="text"
                      name="taxOffice"
                      value={formData.taxOffice}
                      onChange={handleChange}
                      placeholder="Vergi Dairesi *"
                      required={formData.invoiceType === 'corporate'}
                      className="input-field"
                    />
                    <input
                      type="text"
                      name="taxNumber"
                      value={formData.taxNumber}
                      onChange={handleChange}
                      maxLength="11"
                      placeholder="Vergi Numarası (VKN/TCKN) *"
                      required={formData.invoiceType === 'corporate'}
                      className="input-field"
                    />
                  </div>
                )}

                {/* Fatura Adresi Teslimat Adresinden Farklı Olsun Seçeneği */}
                <div className="pt-2 border-t border-gray-200/80">
                  <label className="flex items-center gap-3 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      name="isSeparateBillingAddress"
                      checked={formData.isSeparateBillingAddress}
                      onChange={(e) => setFormData({ ...formData, isSeparateBillingAddress: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900"
                    />
                    <span className="text-xs font-semibold text-gray-800 tracking-wide uppercase">
                      Fatura adresim teslimat adresimden farklı olsun
                    </span>
                  </label>

                  {/* Ayrı Fatura Adresi Formu */}
                  {formData.isSeparateBillingAddress && (
                    <div className="mt-6 pt-6 border-t border-gray-200 grid grid-cols-1 md:grid-cols-2 gap-4 animate-fadeIn">
                      <div className="md:col-span-2 flex items-center gap-2 text-xs font-bold text-gray-700 uppercase tracking-wider mb-1">
                        <FiMapPin className="text-primary text-sm" />
                        <span>Ayrı Fatura Adresi Bilgileri</span>
                      </div>
                      <input
                        type="text"
                        name="billingFirstName"
                        value={formData.billingFirstName}
                        onChange={handleChange}
                        placeholder="Fatura Alıcısı Adı *"
                        required={formData.isSeparateBillingAddress}
                        className="input-field"
                      />
                      <input
                        type="text"
                        name="billingLastName"
                        value={formData.billingLastName}
                        onChange={handleChange}
                        placeholder="Fatura Alıcısı Soyadı *"
                        required={formData.isSeparateBillingAddress}
                        className="input-field"
                      />
                      <input
                        type="tel"
                        name="billingPhone"
                        value={formData.billingPhone}
                        onChange={handleChange}
                        placeholder="Fatura Telefonu *"
                        required={formData.isSeparateBillingAddress}
                        className="input-field"
                      />
                      <input
                        type="text"
                        name="billingCity"
                        value={formData.billingCity}
                        onChange={handleChange}
                        placeholder="Fatura Şehri *"
                        required={formData.isSeparateBillingAddress}
                        className="input-field"
                      />
                      <input
                        type="text"
                        name="billingDistrict"
                        value={formData.billingDistrict}
                        onChange={handleChange}
                        placeholder="Fatura İlçesi *"
                        required={formData.isSeparateBillingAddress}
                        className="input-field"
                      />
                      <input
                        type="text"
                        name="billingPostalCode"
                        value={formData.billingPostalCode}
                        onChange={handleChange}
                        placeholder="Posta Kodu"
                        className="input-field"
                      />
                      <textarea
                        name="billingAddressLine"
                        value={formData.billingAddressLine}
                        onChange={handleChange}
                        placeholder="Fatura Adres Detayı *"
                        required={formData.isSeparateBillingAddress}
                        rows="3"
                        className="input-field md:col-span-2"
                      ></textarea>
                    </div>
                  )}
                </div>
              </div>

              {/* Ödeme Yöntemi */}
              <div className="bg-gray-50 p-8">
                <h2 className="font-serif text-2xl font-light tracking-wide mb-6 flex items-center gap-3">
                  <FiLock className="text-primary" />
                  {t('checkout.payment.title')}
                </h2>
                <div className="space-y-3">
                  {paymentMethods?.creditCard?.enabled && (
                    <div className="border border-gray-200 bg-white">
                      <label className="flex items-center gap-4 p-4 cursor-pointer hover:border-gray-400 transition">
                        <input
                          type="radio"
                          name="paymentMethod"
                          value="credit-card"
                          checked={formData.paymentMethod === 'credit-card'}
                          onChange={handleChange}
                          className="w-4 h-4 accent-gray-900"
                        />
                        <span className="font-light tracking-wide">
                          {paymentMethods.creditCard.label || t('checkout.payment.creditCard')} (Garanti 3D POS)
                        </span>
                      </label>

                      {formData.paymentMethod === 'credit-card' && (
                        <div className="p-5 border-t border-gray-100 bg-gray-50/50 space-y-4 animate-fadeIn">
                          <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1">Kart Üzerindeki İsim</label>
                            <input
                              type="text"
                              name="cardholdername"
                              value={formData.cardholdername}
                              onChange={handleChange}
                              placeholder="Ad Soyad"
                              required={formData.paymentMethod === 'credit-card'}
                              className="input-field bg-white"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-700 mb-1">Kart Numarası</label>
                            <input
                              type="text"
                              name="cardnumber"
                              maxLength="19"
                              value={formData.cardnumber}
                              onChange={(e) => {
                                const val = e.target.value.replace(/\D/g, '').replace(/(.{4})/g, '$1 ').trim();
                                setFormData(prev => ({ ...prev, cardnumber: val }));
                              }}
                              placeholder="0000 0000 0000 0000"
                              required={formData.paymentMethod === 'credit-card'}
                              className="input-field bg-white font-mono"
                            />
                          </div>
                          <div className="grid grid-cols-3 gap-3">
                            <div>
                              <label className="block text-xs font-semibold text-gray-700 mb-1">Son Kullanma Ay</label>
                              <input
                                type="text"
                                name="cardexpiredatemonth"
                                maxLength="2"
                                value={formData.cardexpiredatemonth}
                                onChange={handleChange}
                                placeholder="AA (Örn: 05)"
                                required={formData.paymentMethod === 'credit-card'}
                                className="input-field bg-white text-center font-mono"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-gray-700 mb-1">Son Kullanma Yıl</label>
                              <input
                                type="text"
                                name="cardexpiredateyear"
                                maxLength="2"
                                value={formData.cardexpiredateyear}
                                onChange={handleChange}
                                placeholder="YY (Örn: 28)"
                                required={formData.paymentMethod === 'credit-card'}
                                className="input-field bg-white text-center font-mono"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-semibold text-gray-700 mb-1">CVC / CVV</label>
                              <input
                                type="text"
                                name="cardcvv2"
                                maxLength="4"
                                value={formData.cardcvv2}
                                onChange={handleChange}
                                placeholder="3 Haneli Kod"
                                required={formData.paymentMethod === 'credit-card'}
                                className="input-field bg-white text-center font-mono"
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {paymentMethods?.bankTransfer?.enabled && (
                    <label className="flex items-center gap-4 p-4 border border-gray-200 cursor-pointer hover:border-gray-400 transition bg-white">
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="bank-transfer"
                        checked={formData.paymentMethod === 'bank-transfer'}
                        onChange={handleChange}
                        className="w-4 h-4"
                      />
                      <span className="font-light tracking-wide">
                        {paymentMethods.bankTransfer.label || t('checkout.payment.bankTransfer')}
                      </span>
                    </label>
                  )}
                  {paymentMethods?.cashOnDelivery?.enabled && (
                    <label className="flex items-center gap-4 p-4 border border-gray-200 cursor-pointer hover:border-gray-400 transition bg-white">
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="cash-on-delivery"
                        checked={formData.paymentMethod === 'cash-on-delivery'}
                        onChange={handleChange}
                        className="w-4 h-4"
                      />
                      <span className="font-light tracking-wide">
                        {paymentMethods.cashOnDelivery.label || t('checkout.payment.cashOnDelivery')}
                      </span>
                    </label>
                  )}
                </div>
              </div>

              {/* Sipariş Notu */}
              <div className="bg-gray-50 p-8">
                <h2 className="font-serif text-2xl font-light tracking-wide mb-6">
                  {t('checkout.note.title')}
                </h2>
                <textarea
                  name="customerNote"
                  value={formData.customerNote}
                  onChange={handleChange}
                  placeholder={t('checkout.note.placeholder')}
                  rows="4"
                  className="input-field"
                ></textarea>
              </div>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <div className="bg-gray-50 p-8 sticky top-24">
                <h2 className="font-serif text-2xl font-light tracking-wide mb-8">
                  {t('checkout.summary.title')}
                </h2>

                {/* Products */}
                <div className="space-y-4 mb-8 max-h-80 overflow-y-auto">
                  {items.map((item) => {
                    const product = item.product;
                    if (!product) return null;
                    const price = product.salePrice || product.price;

                    return (
                      <div key={item._id} className="flex gap-4 pb-4 border-b border-gray-200">
                        <div className="w-20 h-20 bg-white flex-shrink-0">
                          {product.images && product.images.length > 0 ? (
                            <img
                              src={getProductImage(product.images, 0)}
                              alt={product.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <PlaceholderImage type="product" />
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="font-light text-gray-900 text-sm mb-1 line-clamp-2">
                            {product.name}
                          </p>
                          <p className="text-xs text-gray-500 font-light">
                            {item.quantity} adet × {formatPrice(price)} ₺
                          </p>
                        </div>
                        <span className="font-light text-gray-900 text-sm">
                          {formatPrice(price * item.quantity)} ₺
                        </span>
                      </div>
                    );
                  })}
                </div>

                {/* Totals */}
                <div className="space-y-4 mb-8">
                  <div className="flex justify-between font-light">
                    <span className="text-gray-600">{t('checkout.summary.subtotal')}</span>
                    <span className="text-gray-900">{formatPrice(subtotal)} ₺</span>
                  </div>
                  <div className="flex justify-between font-light">
                    <span className="text-gray-600">{t('checkout.summary.shipping')}</span>
                    <span className="text-gray-900">
                      {shippingCost === 0 ? t('checkout.summary.free') : `${formatPrice(shippingCost)} ₺`}
                    </span>
                  </div>
                  {subtotal < 500 && (
                    <p className="text-xs text-gray-500 font-light">
                      {t('checkout.summary.freeShippingThreshold')}
                    </p>
                  )}
                  <div className="border-t border-gray-200 pt-4 flex justify-between">
                    <span className="font-light text-gray-900 text-lg">{t('checkout.summary.total')}</span>
                    <span className="font-light text-gray-900 text-2xl">{formatPrice(total)} ₺</span>
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary w-full mb-6"
                >
                  {loading ? t('checkout.summary.submitting') : t('checkout.summary.submit')}
                </button>

                {/* Trust Badges */}
                <div className="space-y-3 pt-6 border-t border-gray-200">
                  <div className="flex items-center gap-3 text-sm font-light text-gray-600">
                    <FiShield className="w-5 h-5 text-primary" />
                    {t('cart.benefits.securePayment')}
                  </div>
                  <div className="flex items-center gap-3 text-sm font-light text-gray-600">
                    <svg className="w-5 h-5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 13l4 4L19 7" />
                    </svg>
                    {t('cart.benefits.returnGuarantee')}
                  </div>
                  <div className="flex items-center gap-3 text-sm font-light text-gray-600">
                    <FiTruck className="w-5 h-5 text-primary" />
                    {t('cart.benefits.fastDelivery')}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default Checkout;
